import {
  Button,
  Card,
  CardBody,
  CloseButton,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { LeadTimeStrategy, type PriceStrategy } from '@snap-types/models/enums';
import type { SnapSiteConfigEditFormDataUnvalidated } from '@snap-types/models/snap-site-config';
import type { FieldPathFn } from '@ui/components/form';
import { CircleInfoIcon } from '@ui/components/Icons';
import { Show } from '@ui/components/Show';
import { ConfirmDeleteDialog } from '@ui/features/snap-site-config/screens/edit/components/ConfirmDeleteDialog';
import { FormRowActions } from '@ui/features/snap-site-config/screens/edit/components/FormRowActions';
import { priceStrategyConfigOptionLabels } from '@ui/features/snap-site-config/screens/edit/components/PriceStrategyConfigsForm/data';
import { useCurrentSite } from '@ui/hooks/useCurrentSite';
import { type FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { match } from 'ts-pattern';

import { getAvailableLeadTimeConfigTypes, leadTimeConfigOptions } from './data';
import { ConstantValueForm } from './lead-time-config-types/ConstantValueForm';
import { PlanningLeadTimeForm } from './lead-time-config-types/PlanningLeadTimeForm';
import { RAFFixedMarketForm } from './lead-time-config-types/RAFFixedMarketForm';

export function LeadTimeConfigForm({
  priceStrategy,
  fieldPath,
}: {
  priceStrategy: PriceStrategy;
  fieldPath: FieldPathFn<SnapSiteConfigEditFormDataUnvalidated>;
}) {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();

  const leadTimeConfig = useWatch({
    name: fieldPath('leadTimeConfig'),
    control: form.control,
  });

  return (
    <FormControl>
      <FormLabel>Lead Time Config</FormLabel>

      <Show when={!leadTimeConfig}>
        <EmptyState priceStrategy={priceStrategy} />
      </Show>

      <Show when={!!leadTimeConfig}>
        <FormRow
          onRemove={() => form.setValue(fieldPath('leadTimeConfig'), null)}
          fieldPath={(fieldName: string) =>
            fieldPath(
              `leadTimeConfig.${fieldName}`,
            ) as FieldPath<SnapSiteConfigEditFormDataUnvalidated>
          }
        />
      </Show>

      {leadTimeConfig ? null : (
        <FormRowActions>
          <Button
            mt={4}
            size="sm"
            variant="outline"
            onClick={() => {
              form.setValue(fieldPath('leadTimeConfig'), {
                leadTimeType: LeadTimeStrategy.CONSTANT_VALUE,
                days: '',
              });
            }}
          >
            Add Lead Time Config
          </Button>
        </FormRowActions>
      )}
    </FormControl>
  );
}

function EmptyState({ priceStrategy }: { priceStrategy: PriceStrategy }) {
  const { isOpen, onClose, onToggle } = useDisclosure();

  return (
    <Flex direction="column">
      <Flex
        align="center"
        gap={2}
        p={4}
        rounded="sm"
        bg="snap.teal.50"
        flexWrap="wrap"
      >
        <CircleInfoIcon color="snap.teal.400" />
        <Text fontWeight="bold" fontSize="14px" color="snap.teal.500">
          No Lead Time Config set up for{' '}
          {priceStrategyConfigOptionLabels[priceStrategy]}.
        </Text>

        <Text fontSize="14px">
          By default, SNAP will not suggest a lead time when you quote a part
          that uses this Price Strategy.
        </Text>

        <Button
          variant="outline"
          size="xs"
          colorScheme="teal"
          onClick={onToggle}
        >
          What is a Lead Time Config?
        </Button>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Flex p={4} bg="mw.panelGrey" mt={4} gap={2} rounded="sm">
          <Flex flex="1" direction="column" gap={2}>
            <Text>
              Lead Time Configs allow you to define how lead times are
              calculated for different Price Strategies. You can set up multiple
              configs to handle various lead time scenarios.
            </Text>

            <Text>
              SNAP recommends setting up at least one Lead Time Config for your
              most common Price Strategy. <strong>Constant Value</strong> is a
              good choice if you have a fixed lead time for most of your
              products.
            </Text>
          </Flex>

          <CloseButton onClick={onClose} />
        </Flex>
      </Collapse>
    </Flex>
  );
}

function FormRow({
  onRemove,
  fieldPath,
}: {
  onRemove: () => void;
  fieldPath: FieldPathFn<SnapSiteConfigEditFormDataUnvalidated>;
}) {
  const form = useFormContext();
  const currentSite = useCurrentSite();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const leadTimeConfigTypeFieldPath = fieldPath('leadTimeType');

  const leadTimeConfigType = useWatch({
    name: leadTimeConfigTypeFieldPath,
    control: form.control,
  }) as LeadTimeStrategy;

  const availableLeadTimeConfigTypes = getAvailableLeadTimeConfigTypes(
    currentSite.code,
  );

  return (
    <>
      <Card variant="outline">
        <CardBody gap={4} display="flex">
          <Flex flex="1" gap={4} direction="column">
            <FormControl>
              <FormLabel htmlFor={leadTimeConfigTypeFieldPath}>
                Lead Time Type:
              </FormLabel>

              <Select
                {...form.register(leadTimeConfigTypeFieldPath)}
                id={leadTimeConfigTypeFieldPath}
              >
                {Object.entries(leadTimeConfigOptions)
                  .filter(([key]) =>
                    availableLeadTimeConfigTypes.all.includes(
                      key as LeadTimeStrategy,
                    ),
                  )
                  .map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </Select>
            </FormControl>

            {match(leadTimeConfigType)
              .returnType<React.ReactNode>()
              .with(LeadTimeStrategy.CONSTANT_VALUE, () => (
                <ConstantValueForm fieldPath={fieldPath} />
              ))
              .with(LeadTimeStrategy.PLANNING_LEADTIME, () => (
                <PlanningLeadTimeForm />
              ))
              .with(LeadTimeStrategy.RAF_FIXED_MARKET, () => (
                <RAFFixedMarketForm fieldPath={fieldPath} />
              ))
              .exhaustive()}
          </Flex>

          <CloseButton
            aria-label="Remove Lead Time Config"
            onClick={onOpen}
            opacity={0.4}
            _groupHover={{ opacity: 1 }}
            transition="opacity 0.2s"
          />
        </CardBody>
      </Card>

      <ConfirmDeleteDialog
        isOpen={isOpen}
        onDismiss={onClose}
        onCancel={onClose}
        onConfirm={() => {
          onRemove();
          onClose();
        }}
      />
    </>
  );
}
